import { DatePipe } from '@angular/common';
import { AfterViewInit, Component, QueryList, ViewChildren } from '@angular/core';
import { ScoreDetailService } from '@core/score-detail.service';
import { IPrintableReportHeading } from '@models/report-print-heading';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DateHelpers } from '@shared/date-helpers';
import { ModalTemplateComponent } from '@shared/modals/modal-template/modal-template.component';
import { ScoreDetailViewComponent } from '@shared/score-detail/score-detail-view/score-detail-view.component';
import { ZbpDataTable } from '@shared/zbp-data-table/zbp-data-table.model';
import { zbpIconNames } from '@shared/zbp-icon/zbp-icon-names';
import { IPrintAllScoreDetailsParams } from '../score-detail-modal/score-detail-params';


interface StudentScoreDetailViewModel {
  studentName: string;
  className: string;
  productName: string;
  activityName: string;
  columns: ZbpDataTable.Column[];
  rows: ZbpDataTable.Row[];
  printableHeaders: IPrintableReportHeading[];
}

/** All rows are collapsed on print. */
@Component({
  selector: 'zbp-score-detail-print-all-modal',
  templateUrl: './score-detail-print-all-modal.component.html',
  styleUrls: ['./score-detail-print-all-modal.component.scss'],
  standalone: true,
  imports: [
    ModalTemplateComponent,
    ScoreDetailViewComponent,
    TranslateModule
  ],
  providers: [
    DatePipe
  ]
})
export class ScoreDetailPrintAllModalComponent implements AfterViewInit {
  zbpIconNames = zbpIconNames;
  studentViewModels: StudentScoreDetailViewModel[] = [];

  params: IPrintAllScoreDetailsParams;

  @ViewChildren(ScoreDetailViewComponent) scoreDetailViews!: QueryList<ScoreDetailViewComponent>;

  constructor(
    private activeModal: NgbActiveModal,
    private scoreDetailService: ScoreDetailService,
    private translate: TranslateService,
    private datePipe: DatePipe,
  ) { }

  ngAfterViewInit(): void {
    this.print();
  }

  public static open(ngbModal: NgbModal, params: IPrintAllScoreDetailsParams): NgbModalRef {
    const modal = ngbModal.open(
      ScoreDetailPrintAllModalComponent,
      {
        size: 'lg',
      },
    );
    const component = modal.componentInstance as ScoreDetailPrintAllModalComponent;
    component.params = params;
    component.buildViewModels(params);
    return modal;
  }

  buildViewModels(params: IPrintAllScoreDetailsParams): void {
    this.params = params;
    this.params.students.forEach((student) => {
      /** Parts are not expanded, so we do not need to get the manually scored parts. */
      const vm = this.scoreDetailService.buildTable({
        topicType: this.params.topicType,
        score: student.score,
        benchmark: student.benchmark,
        parts: student.parts,
        isEditing: false,
        productType: this.params.productType,
        manualScoreResponse: null,
      });

      const printableHeaders: IPrintableReportHeading[] = [
        { // Student
          label: this.translate.instant('shared-score-detail-field-student'),
          value: student.studentName ?? ''
        },
        { // Class
          label: this.translate.instant('shared-score-detail-field-class'),
          value: this.params.className
            ? this.params.className
            : this.translate.instant('parent-report-all-classes')
        },
        { // Report
          label: this.translate.instant('parent-report-name-field'),
          value: this.translate.instant('shared-score-detail-header')
        },
        { // Product
          label: this.translate.instant('parent-report-product-field'),
          value: this.params.productName
            ? this.params.productName
            : this.translate.instant('parent-report-all-products')
        },
        { // Activity
          label: this.translate.instant('shared-student-activity-report-activity'),
          value: this.params.activityName ?? ''
        },
      ];
      if (this.params.showCompletedOn) {
        printableHeaders.push(
          { // Date/Time Completed
            label: this.translate.instant('shared-score-detail-field-date-time-completed'),
            value: student.completedOn
              ? this.datePipe.transform(student.completedOn, DateHelpers.DATE_AND_TIME_FORMAT)
              : this.translate.instant('shared-score-detail-field-date-time-completed-empty-message')
          }
        );
      }
      if (this.params.showWordList) {
        printableHeaders.push(
          { // word list level
            label: this.translate.instant('parent-report-wordList-field'),
            value: student.wordListLevel,
          }
        )
      }

      this.studentViewModels.push(
        {
          studentName: student.studentName,
          className: this.params.className,
          productName: this.params.productName,
          activityName: this.params.activityName,
          columns: vm.columns,
          rows: vm.rows,
          printableHeaders,
        }
      );
    });
  }

  dismiss(): void {
    this.activeModal.dismiss();
  }

  print(): void {
    setTimeout(() => {
      window.print();
      // Modal closes immediately after print.
      this.activeModal.dismiss();
    }, 100);
  }
}
